import { useCallback, useMemo } from "react";

import { kernel, kernelPost } from "@lopie/kernel-ui";

/**
 * The calls this app makes, and what becomes of a refusal.
 *
 * `POST /insights/query` answers one of three ways, and the screen says each in
 * its own words:
 *
 * - **200**: an answer, with the tags that decided it (`permitted_tags`) and
 *   when the copy it came from is as of.
 * - **400 with a code** (`field_not_allowed`, `money_needs_currency`, ...): the
 *   *question* is wrong, and the kernel names the part. That is the person's to
 *   fix, so it is shown as a sentence beside the form and never as an error.
 * - **anything else**: the kernel or the query service could not answer, which
 *   is a fact about the platform and not about the data. It is never drawn as
 *   an empty chart.
 */
export function useKernel(token) {
  const get = useMemo(() => kernel(token), [token]);
  const post = useMemo(() => kernelPost(token), [token]);

  const query = useCallback(async (spec) => {
    try {
      return { ok: true, result: await post("/insights/query", { spec }) };
    } catch (error) {
      return { ok: false, error };
    }
  }, [post]);

  // The records behind a mark: ids from the copy, each read through the kernel,
  // and one audit row written before any are returned.
  const drill = useCallback(async (spec, from) => {
    try {
      return { ok: true, result: await post("/insights/drill", { spec, from }) };
    } catch (error) {
      return { ok: false, error };
    }
  }, [post]);

  // Saved reports and dashboards, which the kernel keeps and decides who may open.
  const reports = useMemo(() => ({
    list: () => get("/insights/reports"),
    create: (body) => post("/insights/reports", body),
    edit: (id, body) => post(`/insights/reports/${id}`, body),
    share: (id, body) => post(`/insights/reports/${id}/share`, body),
    rollback: (id, body) => post(`/insights/reports/${id}/rollback`, body),
    remove: (id, body) => post(`/insights/reports/${id}/delete`, body),
    schedule: (id, body) => post(`/insights/reports/${id}/schedule`, body),
    alerts: (id, body) => post(`/insights/reports/${id}/alerts`, body),
  }), [get, post]);

  // A question in words. The model's answer is a spec to be checked, never a figure.
  const ask = useCallback(async (question) => {
    try {
      return { ok: true, result: await post("/insights/ask", { question }) };
    } catch (error) {
      return { ok: false, error };
    }
  }, [post]);

  return { get, query, drill, reports, ask };
}

/** A refusal of the question, as opposed to a failure to answer it. */
export const isBadQuestion = (error) =>
  error?.status === 400 && typeof error.error === "string" && error.error !== "body_is_not_json";
